import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Users, Clock, Plane, Loader2, ArrowRight, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { addDaysLocal, getLocalToday } from '../utils/dateUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const positionLabels = { captain: 'Capitan', first_officer: 'Copiloto', flight_attendant: 'FA', flight_engineer: 'Ing. Vuelo' };
const statusColors = { ok: 'text-emerald-500', warning: 'text-amber-500', exceeded: 'text-red-500' };
const flightBarColors = ['bg-blue-500', 'bg-cyan-500', 'bg-indigo-500', 'bg-violet-500', 'bg-teal-500'];

function getWeekDays(startDate) {
    const days = [];
    const [y, m, d] = startDate.split('-').map(Number);
    const start = new Date(y, m - 1, d);
    const dow = start.getDay();
    const monday = new Date(y, m - 1, d - dow + (dow === 0 ? -6 : 1));
    for (let i = 0; i < 7; i++) {
        days.push(new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + i));
    }
    return days;
}

function formatDateLocal(d) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function CrewCalendarPage() {
    const { token } = useAuth();
    const headers = { Authorization: `Bearer ${token}` };
    const [calendar, setCalendar] = useState([]);
    const [loading, setLoading] = useState(true);
    const [weekStart, setWeekStart] = useState(() => {
        const d = new Date();
        const dow = d.getDay();
        const monday = new Date(d.getFullYear(), d.getMonth(), d.getDate() - dow + (dow === 0 ? -6 : 1));
        return formatDateLocal(monday);
    });
    const [posFilter, setPosFilter] = useState('all');

    const weekDays = getWeekDays(weekStart);
    const startDate = formatDateLocal(weekDays[0]);
    const endDate = formatDateLocal(weekDays[6]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            let url = `${API}/crew/calendar?start_date=${startDate}&end_date=${endDate}`;
            if (posFilter !== 'all') url += `&position=${posFilter}`;
            const res = await axios.get(url, { headers });
            setCalendar(res.data);
        } catch { toast.error('Error al cargar calendario'); }
        finally { setLoading(false); }
    }, [startDate, endDate, posFilter]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const shiftWeek = (dir) => {
        setWeekStart(addDaysLocal(weekStart, dir * 7));
    };

    const getFlightPosition = (flight, day) => {
        const depDate = flight.scheduled_departure?.split('T')[0];
        const dayStr = formatDateLocal(day);
        return depDate === dayStr;
    };

    const getFlightTimeSpan = (flight) => {
        try {
            const dep = new Date(flight.scheduled_departure);
            const arr = new Date(flight.scheduled_arrival);
            const startHour = dep.getHours() + dep.getMinutes() / 60;
            const endHour = arr.getHours() + arr.getMinutes() / 60;
            const left = (startHour / 24) * 100;
            const width = Math.max(((endHour - startHour) / 24) * 100, 5);
            return { left: `${left}%`, width: `${width}%` };
        } catch { return { left: '0%', width: '10%' }; }
    };

    return (
        <div className="space-y-6" data-testid="crew-calendar-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-4xl font-bold tracking-tight uppercase font-['Barlow_Condensed']" data-testid="crew-calendar-title">Calendario Tripulacion</h1>
                    <p className="text-muted-foreground mt-1">Asignaciones semanales y FDTL</p>
                </div>
                <div className="flex items-center gap-2">
                    <Select value={posFilter} onValueChange={setPosFilter}>
                        <SelectTrigger className="w-[150px]" data-testid="pos-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Todas</SelectItem>
                            <SelectItem value="captain">Capitan</SelectItem>
                            <SelectItem value="first_officer">Copiloto</SelectItem>
                            <SelectItem value="flight_attendant">FA</SelectItem>
                            <SelectItem value="flight_engineer">Ing. Vuelo</SelectItem>
                        </SelectContent>
                    </Select>
                    <Button variant="outline" size="icon" onClick={() => shiftWeek(-1)} data-testid="prev-week"><ChevronLeft className="w-4 h-4" /></Button>
                    <span className="text-sm font-mono font-bold px-2 whitespace-nowrap">{startDate} — {endDate}</span>
                    <Button variant="outline" size="icon" onClick={() => shiftWeek(1)} data-testid="next-week"><ChevronRight className="w-4 h-4" /></Button>
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
            ) : calendar.length === 0 ? (
                <Card className="card-aviation"><CardContent className="p-12 text-center text-muted-foreground"><Users className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>No hay tripulantes activos</p></CardContent></Card>
            ) : (
                <div className="overflow-x-auto">
                    <div className="min-w-[900px]">
                        {/* Header */}
                        <div className="grid grid-cols-[200px_repeat(7,1fr)] gap-0 border-b border-border/50 pb-2 mb-2">
                            <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground px-2">Tripulante</div>
                            {weekDays.map((d, i) => {
                                const isToday = formatDateLocal(d) === getLocalToday();
                                return (
                                    <div key={i} className={`text-center text-xs font-bold uppercase ${isToday ? 'text-primary' : 'text-muted-foreground'}`}>
                                        {d.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric' })}
                                        {isToday && <div className="w-1.5 h-1.5 rounded-full bg-primary mx-auto mt-0.5" />}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Rows */}
                        <div className="space-y-1" data-testid="calendar-rows">
                            {calendar.map((member, mi) => (
                                <div key={member.crew_id} className="grid grid-cols-[200px_repeat(7,1fr)] gap-0 items-center group hover:bg-muted/20 rounded-lg transition-colors" data-testid={`calendar-row-${member.crew_id}`}>
                                    {/* Crew info */}
                                    <div className="px-2 py-2">
                                        <p className="text-sm font-medium truncate">{member.name}</p>
                                        <div className="flex items-center gap-2 text-[10px]">
                                            <Badge variant="outline" className="text-[9px] py-0">{positionLabels[member.position] || member.position}</Badge>
                                            <span className={`font-mono font-bold ${statusColors[member.fdtl_status]}`}>
                                                {member.total_flight_hours}h
                                            </span>
                                            {member.fdtl_status === 'warning' && <AlertTriangle className="w-3 h-3 text-amber-500" />}
                                            {member.fdtl_status === 'exceeded' && <AlertTriangle className="w-3 h-3 text-red-500" />}
                                        </div>
                                    </div>

                                    {/* Day cells */}
                                    {weekDays.map((day, di) => {
                                        const dayFlights = member.flights.filter(f => getFlightPosition(f, day));
                                        return (
                                            <div key={di} className="relative min-h-[60px] border-l border-border/20 py-1">
                                                {dayFlights.map((f, fi) => {
                                                    const color = flightBarColors[(mi + fi) % flightBarColors.length];
                                                    const depTime = f.scheduled_departure?.split('T')[1]?.slice(0,5) || '';
                                                    const arrTime = f.scheduled_arrival?.split('T')[1]?.slice(0,5) || '';
                                                    return (
                                                        <div
                                                            key={f.flight_id}
                                                            className={`${color} rounded-md flex flex-col justify-center px-2 py-1 text-white font-mono font-bold overflow-hidden cursor-default mb-1`}
                                                            title={`${f.flight_number} ${f.origin}-${f.destination} ${depTime}-${arrTime}`}
                                                        >
                                                            <div className="text-xs leading-tight">{f.flight_number}</div>
                                                            <div className="text-[10px] opacity-80 leading-tight">{f.origin}-{f.destination}</div>
                                                            <div className="text-[10px] opacity-70 leading-tight">{depTime}-{arrTime}</div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
